"use client";

const SEARCH_ANALYTICS_VIEW_NODE_ID_PREFIX = "SearchAnalyticsView:";
const SEARCH_ANALYTICS_PROJECT_NODE_ID_PREFIX = "Project:";
const USER_ANALYTICS_VIEW_NODE_ID_PREFIX = "UserAnalyticsView:";
const USER_ANALYTICS_PROJECT_NODE_ID_PREFIX = "Project:";
const DASHBOARD_NODE_ID_PREFIX = "Dashboard:";
const INDEX_WORKER_NODE_ID_PREFIX = "IndexWorker:";
const INDEX_WORKER_PROJECT_NODE_ID_PREFIX = "Project:";

const ANALYTICS_PROJECT_TARGET_SEGMENTS = new Set(["default", "board", "list"]);

type MemoListCardHostRoute = {
  workspaceId: string;
  projectId: string;
  featureKind: "SEARCH_ANALYTICS" | "USER_ANALYTICS" | "INDEX_WORKER";
};

type MemoTargetRoute = {
  workspaceId: string;
  projectId: string;
} & (
  | {
      featureKind: "SEARCH_ANALYTICS";
      targetKind: "SEARCH_ANALYTICS_PROJECT";
      targetId: string;
    }
  | {
      featureKind: "SEARCH_ANALYTICS";
      targetKind: "SEARCH_ANALYTICS_VIEW";
      targetId: string;
    }
  | {
      featureKind: "USER_ANALYTICS";
      targetKind: "USER_ANALYTICS_PROJECT";
      targetId: string;
    }
  | {
      featureKind: "USER_ANALYTICS";
      targetKind: "USER_ANALYTICS_VIEW";
      targetId: string;
    }
  | {
      featureKind: "DASHBOARD";
      targetKind: "DASHBOARD";
      targetId: string;
    }
  | {
      featureKind: "INDEX_WORKER";
      targetKind: "INDEX_WORKER_PROJECT";
      targetId: string;
    }
  | {
      featureKind: "INDEX_WORKER";
      targetKind: "INDEX_WORKER";
      targetId: string;
    }
);

export type MemoContextKeyInput = {
  workspaceId: string;
  projectId: string;
  featureKind:
    | "SEARCH_ANALYTICS"
    | "USER_ANALYTICS"
    | "INDEX_WORKER"
    | "DASHBOARD";
  targetKind: MemoTargetRoute["targetKind"];
  targetId: string;
};

/**
 * pathname 由来の segment を GraphQL Node ID として扱える文字列に戻します。
 * `usePathname` は `%3D` を含む encoded ID を返すことがあるため、memo API に渡す前に復号します。
 */
function decodedUserMemoPathSegment(segment: string) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * SearchAnalyticsView の GraphQL Node ID だけを user memo の target として許可します。
 * `default` などの疑似 view ID や encoded のままの ID を API に渡して 400 を起こさないための境界です。
 */
export function isSearchAnalyticsViewNodeId(value: string) {
  try {
    return atob(value).startsWith(SEARCH_ANALYTICS_VIEW_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/**
 * Search Analytics project の GraphQL Node ID だけを /default memo target として許可します。
 * 生の project slug を targetID として送らないため、route の GraphQL ID 境界をここで閉じます。
 */
export function isSearchAnalyticsProjectNodeId(value: string) {
  try {
    return atob(value).startsWith(SEARCH_ANALYTICS_PROJECT_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** UserAnalyticsView の GraphQL Node ID だけを UA memo target として許可します。 */
export function isUserAnalyticsViewNodeId(value: string) {
  try {
    return atob(value).startsWith(USER_ANALYTICS_VIEW_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** User Analytics project の GraphQL Node ID だけを /default memo target として許可します。 */
export function isUserAnalyticsProjectNodeId(value: string) {
  try {
    return atob(value).startsWith(USER_ANALYTICS_PROJECT_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** Dashboard の GraphQL Node ID だけを Dashboard page memo target として許可します。 */
export function isDashboardNodeId(value: string) {
  try {
    return atob(value).startsWith(DASHBOARD_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** IndexWorker の GraphQL Node ID だけを IW memo target として許可します。 */
function isIndexWorkerNodeId(value: string) {
  try {
    return atob(value).startsWith(INDEX_WORKER_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/** Index Worker project の GraphQL Node ID だけを IW memo context として許可します。 */
function isIndexWorkerProjectNodeId(value: string) {
  try {
    return atob(value).startsWith(INDEX_WORKER_PROJECT_NODE_ID_PREFIX);
  } catch {
    return false;
  }
}

/**
 * 一覧 card が置かれる board/list route の workspace・project・feature 境界を復元します。
 * route 自体が page-level memo target を持たない IW list でも、card 起点の memo intent を維持する判断に使います。
 */
export function memoListCardHostFromPathname(
  pathname: string,
): MemoListCardHostRoute | null {
  const segments = pathname.split("/").filter(Boolean);
  const rawWorkspaceId = segments[1];
  const featureIndex = segments.findIndex(
    (segment) => segment === "sa" || segment === "ua" || segment === "iw",
  );
  const featureSegment = featureIndex >= 0 ? segments[featureIndex] : undefined;
  const rawProjectId =
    featureIndex >= 0 ? segments[featureIndex + 1] : undefined;
  const rawViewId = featureIndex >= 0 ? segments[featureIndex + 2] : undefined;

  if (rawViewId !== "board" && rawViewId !== "list") {
    return null;
  }
  if (!rawWorkspaceId || !rawProjectId || !featureSegment) {
    return null;
  }

  const workspaceId = decodedUserMemoPathSegment(rawWorkspaceId);
  const projectId = decodedUserMemoPathSegment(rawProjectId);
  if (!workspaceId || !projectId) {
    return null;
  }

  if (featureSegment === "sa") {
    return { workspaceId, projectId, featureKind: "SEARCH_ANALYTICS" };
  }
  if (featureSegment === "ua") {
    return { workspaceId, projectId, featureKind: "USER_ANALYTICS" };
  }
  return { workspaceId, projectId, featureKind: "INDEX_WORKER" };
}

/**
 * 現在の URL が Search Analytics の全体 view または保存済み view 詳細かを判定します。
 * route group は pathname に出ないため、公開 URL の segment だけで User memo 用 context を復元します。
 */
export function memoTargetFromPathname(
  pathname: string,
): MemoTargetRoute | null {
  const segments = pathname.split("/").filter(Boolean);
  const rawWorkspaceId = segments[1];
  const dashboardIndex = segments.findIndex(
    (segment) => segment === "dashboards",
  );
  const rawDashboardId =
    dashboardIndex >= 0 ? segments[dashboardIndex + 1] : undefined;
  if (rawWorkspaceId && rawDashboardId) {
    const workspaceId = decodedUserMemoPathSegment(rawWorkspaceId);
    const targetSegment = decodedUserMemoPathSegment(rawDashboardId);
    if (!workspaceId || !targetSegment || !isDashboardNodeId(targetSegment)) {
      return null;
    }

    return {
      workspaceId,
      projectId: "",
      featureKind: "DASHBOARD",
      targetKind: "DASHBOARD",
      targetId: targetSegment,
    };
  }

  const featureIndex = segments.findIndex(
    (segment) => segment === "sa" || segment === "ua" || segment === "iw",
  );
  const featureSegment = featureIndex >= 0 ? segments[featureIndex] : undefined;
  const rawProjectId =
    featureIndex >= 0 ? segments[featureIndex + 1] : undefined;
  const rawViewId = featureIndex >= 0 ? segments[featureIndex + 2] : undefined;

  if (!rawWorkspaceId || !rawProjectId || !rawViewId || !featureSegment) {
    return null;
  }

  const workspaceId = decodedUserMemoPathSegment(rawWorkspaceId);
  const projectId = decodedUserMemoPathSegment(rawProjectId);
  const targetSegment = decodedUserMemoPathSegment(rawViewId);
  if (!workspaceId || !projectId || !targetSegment) {
    return null;
  }

  if (featureSegment === "sa") {
    if (ANALYTICS_PROJECT_TARGET_SEGMENTS.has(targetSegment)) {
      if (!isSearchAnalyticsProjectNodeId(projectId)) {
        return null;
      }

      return {
        workspaceId,
        projectId,
        featureKind: "SEARCH_ANALYTICS",
        targetKind: "SEARCH_ANALYTICS_PROJECT",
        targetId: projectId,
      };
    }

    if (!isSearchAnalyticsViewNodeId(targetSegment)) {
      return null;
    }

    return {
      workspaceId,
      projectId,
      featureKind: "SEARCH_ANALYTICS",
      targetKind: "SEARCH_ANALYTICS_VIEW",
      targetId: targetSegment,
    };
  }

  if (featureSegment === "iw") {
    if (!isIndexWorkerProjectNodeId(projectId)) {
      return null;
    }

    if (ANALYTICS_PROJECT_TARGET_SEGMENTS.has(targetSegment)) {
      return {
        workspaceId,
        projectId,
        featureKind: "INDEX_WORKER",
        targetKind: "INDEX_WORKER_PROJECT",
        targetId: projectId,
      };
    }

    if (!isIndexWorkerNodeId(targetSegment)) {
      return null;
    }

    return {
      workspaceId,
      projectId,
      featureKind: "INDEX_WORKER",
      targetKind: "INDEX_WORKER",
      targetId: targetSegment,
    };
  }

  if (ANALYTICS_PROJECT_TARGET_SEGMENTS.has(targetSegment)) {
    if (!isUserAnalyticsProjectNodeId(projectId)) {
      return null;
    }

    return {
      workspaceId,
      projectId,
      featureKind: "USER_ANALYTICS",
      targetKind: "USER_ANALYTICS_PROJECT",
      targetId: projectId,
    };
  }

  if (!isUserAnalyticsViewNodeId(targetSegment)) {
    return null;
  }

  return {
    workspaceId,
    projectId,
    featureKind: "USER_ANALYTICS",
    targetKind: "USER_ANALYTICS_VIEW",
    targetId: targetSegment,
  };
}

/**
 * User memo side panel の target 同一性を feature と target kind まで含めて表します。
 * UA/SA default は同じ Project Node ID を共有できるため、targetId 単体では比較しません。
 */
export function memoContextKey(context: MemoContextKeyInput) {
  return [
    context.workspaceId,
    context.projectId,
    context.featureKind,
    context.targetKind,
    context.targetId,
  ].join("\u001f");
}
